import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const formatServiceName = (name) =>
  name
    ? name.split(/[-_]/).map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(" ")
    : "";

const formatCurrency = (value) =>
  typeof value === "number" && value > 0
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
    : "—";

/** X-axis ticks: compact $ labels (same as Cost chart) */
const formatAxisUsd = (v) => {
  if (typeof v !== "number" || !Number.isFinite(v)) return "";
  if (v >= 1000) return `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  if (Number.isInteger(v)) return `$${v}`;
  return `$${v.toFixed(0)}`;
};

/** "MigrateToGraviton" -> "Migrate To Graviton" */
const formatActionLabel = (key) => {
  if (!key) return "—";
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

/**
 * Per-service rows from summary `services[]`: bar value = sum of that row's `cloud_waste_savings_by_action`.
 */
function buildPerServiceSavings(services) {
  const list = Array.isArray(services) ? services : [];
  const rows = [];

  list.forEach((row) => {
    const raw = row?.cloud_waste_savings_by_action;
    if (!raw || typeof raw !== "object") return;

    const byAction = {};
    let total = 0;
    Object.entries(raw).forEach(([actionKey, v]) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return;
      byAction[actionKey] = n;
      total += n;
    });

    if (total <= 0) return;

    const serviceKey = row.service ?? row.name ?? "";
    rows.push({
      name: formatServiceName(row.service ?? row.name) || serviceKey || "—",
      serviceKey,
      value: total,
      byAction,
    });
  });

  return rows.sort((a, b) => b.value - a.value).slice(0, 8);
}

function SummaryCloudWasteChart({ services, onServiceBarClick, activeServiceKey = "" }) {
  const data = useMemo(() => buildPerServiceSavings(services), [services]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;

    const entries = Object.entries(d.byAction || {}).sort((a, b) => b[1] - a[1]);
    const multi = entries.length > 1;

    return (
      <Box sx={{ ...t.chartTooltip, minWidth: multi ? 180 : undefined }}>
        <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.75rem" }}>
          {d.name}
        </Typography>
        {multi ? (
          <>
            <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", display: "block", mt: 0.5 }}>
              {entries.map(([key, amt], i) => (
                <span key={key}>
                  {i > 0 ? " · " : ""}
                  {formatActionLabel(key)}: {formatCurrency(amt)}
                </span>
              ))}
            </Typography>
            <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", display: "block", mt: 0.25 }}>
              Total: {formatCurrency(d.value)}
            </Typography>
          </>
        ) : (
          <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", ml: 0.5, display: "block" }}>
            {entries[0] ? `${formatActionLabel(entries[0][0])}: ` : ""}
            {formatCurrency(d.value)}
          </Typography>
        )}
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No Est. Monthly Savings by service
        </Typography>
      </Box>
    );
  }

  /* Single category fills the plot height in Recharts; fixed barSize avoids one huge bar */
  const barProps =
    data.length === 1
      ? { barSize: 24 }
      : { maxBarSize: data.length <= 4 ? 32 : 24 };

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        height: "100%",
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: t.colors.text,
          fontSize: "0.875rem",
          mb: 1,
          flexShrink: 0,
        }}
      >
        Cloud Waste (Est. Monthly Savings)
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          width: "100%",
          position: "relative",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
            barCategoryGap={data.length <= 4 ? "12%" : "8%"}
            onClick={(state) => {
              if (!onServiceBarClick || !state?.activePayload?.length) return;
              const key = state.activePayload[0]?.payload?.serviceKey;
              if (key) onServiceBarClick(key);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={t.colors.borderLight} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: t.colors.textMuted }} tickFormatter={formatAxisUsd} />
            <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 10, fill: t.colors.text }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
            <Bar
              dataKey="value"
              nameKey="name"
              radius={[0, 4, 4, 0]}
              fill={t.colors.success}
              cursor={onServiceBarClick ? "pointer" : "default"}
              {...barProps}
            >
              {data.map((entry) => {
                const hasSel = Boolean(activeServiceKey);
                const selected = hasSel && entry.serviceKey === activeServiceKey;
                return (
                  <Cell
                    key={entry.serviceKey || entry.name}
                    fill={t.colors.success}
                    fillOpacity={hasSel ? (selected ? 1 : 0.22) : 1}
                    stroke={selected ? t.colors.accent : "transparent"}
                    strokeWidth={selected ? 2.5 : 0}
                    style={
                      hasSel
                        ? selected
                          ? { filter: "drop-shadow(0 0 10px rgba(59,130,246,0.45))" }
                          : { filter: "blur(1.2px)" }
                        : undefined
                    }
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default SummaryCloudWasteChart;
